
type LogLevel = 'error' | 'warn' | 'info' | 'debug';

interface LoggerOptions {
  enabled: boolean;
  level: LogLevel;
  prefix?: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3
};

// Default configuration based on environment
const defaultOptions: LoggerOptions = {
  enabled: process.env.NODE_ENV !== 'production',
  level: process.env.NODE_ENV === 'production' ? 'error' : 'info'
};

// Allow configuring log level via localStorage for debugging
try {
  const storedLevel = localStorage.getItem('log_level');
  if (storedLevel && LOG_LEVELS[storedLevel as LogLevel] !== undefined) {
    defaultOptions.level = storedLevel as LogLevel;
  }
} catch (e) {
  // Ignore localStorage errors (might be in SSR context)
}

class Logger {
  private options: LoggerOptions;

  constructor(options: Partial<LoggerOptions> = {}) {
    this.options = { ...defaultOptions, ...options };
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.options.enabled) return false;
    return LOG_LEVELS[level] <= LOG_LEVELS[this.options.level];
  }

  private formatMessage(message: any): any {
    if (typeof message === 'string' && this.options.prefix) {
      return `${this.options.prefix}: ${message}`;
    }
    return message;
  }

  // Format objects for logging
  private formatArgs(args: any[]): any[] {
    return args.map(arg => {
      // First argument gets prefix if it's a string
      if (arg === args[0] && typeof arg === 'string' && this.options.prefix) {
        return this.formatMessage(arg);
      }
      
      // Format arrays and objects to be more readable in logs
      if (arg && typeof arg === 'object') {
        try {
          // We don't modify the original object, just adapt it for logging
          return arg;
        } catch {
          return arg; // Fallback to original if it can't be processed
        }
      }
      return arg;
    });
  }

  error(...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(...this.formatArgs(args));
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...this.formatArgs(args));
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      console.info(...this.formatArgs(args));
    }
  }

  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.debug(...this.formatArgs(args));
    }
  }

  // Create a child logger with a specific prefix
  child(prefix: string): Logger {
    return new Logger({
      ...this.options,
      prefix: this.options.prefix 
        ? `${this.options.prefix}:${prefix}`
        : prefix
    });
  }
}

// Create the default logger instance
export const logger = new Logger();

// Export factory function for creating component-specific loggers
export const createLogger = (prefix: string) => logger.child(prefix);
